<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import type { StyleValue } from 'vue';

type OverscrollBehavior = 'auto' | 'contain' | 'none';

interface Props {
  light?: boolean;
  overscrollBehavior?: OverscrollBehavior | [OverscrollBehavior, OverscrollBehavior];
  scroll?: 'x' | 'y' | 'both';
}

const props = withDefaults(defineProps<Props>(), {
  light: false,
  scroll: 'both',
  overscrollBehavior: 'auto',
});

const styles = computed(() => {
  const result: Partial<StyleValue> = {};

  switch (props.scroll) {
    case 'x':
      result.overflowX = 'auto';
      result.overflowY = 'hidden';
      break;
    case 'y':
      result.overflowY = 'auto';
      result.overflowX = 'hidden';
      break;
    default:
      result.overflow = 'auto';
      break;
  }

  result.overscrollBehavior = Array.isArray(props.overscrollBehavior)
    ? props.overscrollBehavior.join(' ')
    : props.overscrollBehavior;

  return result;
});

const isWindows = ref(false);

onMounted(() => {
  const ua = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || '';

  isWindows.value = /windows/i.test(ua) || /^win/i.test(platform);
});
</script>

<template>
  <div :class="[$style.root, { [$style.base]: isWindows, [$style.light]: isWindows && props.light }]" :style="styles">
    <slot />
  </div>
</template>

<style lang="scss" module>
.root {
  -webkit-overflow-scrolling: touch;
  will-change: scroll-position;
}

.base {
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar-corner,
  &::-webkit-resizer {
    background-color: #f6f6f8;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: transparent;
  }

  &::-webkit-scrollbar-button {
    height: 0;
    width: 0;
    display: none;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    width: 2px;
    border-radius: 8px;
    background-color: transparent;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: #d7d9e2;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #9aa0b6;
  }

  ::-webkit-scrollbar-track,
  ::-webkit-scrollbar-track-piece {
    margin: 0;
  }
}

.light {
  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar-corner,
  &::-webkit-resizer {
    background-color: transparent;
  }
}
</style>
